import i18n from '@risksmart-app/i18n/src/i18n';
import { z } from 'zod';

import type { CycleCheckContext } from './conditionalLogicGraph';
import { findCycleVertices } from './conditionalLogicGraph';
import type { FieldSummary } from './constants';
import { buildRequiredFieldSectionRuleError } from './requiredFieldGuard';
import type {
  ConditionalLogicOption,
  FieldTypeConditionMetadata,
} from './types';

export const CONDITIONAL_OPERATOR_LABELS: Record<string, string> = {
  '=': 'Is equal to',
  '!=': 'Is not equal to',
  ':': 'Includes',
  '!:': 'Does not include',
};

// On a text field `:` is a substring test, not list membership (CUS-373).
export const FREE_TEXT_CONDITIONAL_OPERATOR_LABELS: Record<string, string> = {
  ':': 'Contains',
  '!:': 'Does not contain',
};

const conditionalLogicRulesSchema = z
  .array(
    z.object({
      ifField: z.string(),
      operator: z.string().optional(),
      values: z.array(z.string()),
      // True for a code-declared read-only default rule surfaced in merge mode.
      // Locked rows render non-editable and are stripped before persisting;
      // only customer (unlocked) rules are saved.
      locked: z.boolean().optional(),
    })
  )
  .optional()
  .default([]);

const conditionalLogicOperationSchema = z.enum(['and', 'or']).default('and');

const optionSchema = z.object({
  id: z.string(),
  label: z.string().min(1, 'Option label is required'),
  altValue: z.string().optional(),
});

// `id` is not a React key: it is the value persisted against records, and
// conditional-logic rule values are built from it.
export const resolveOptionStoredValue = (
  option: { altValue?: string; id: string; label: string },
  isShowingAltValues: boolean
): string =>
  isShowingAltValues ? option.altValue?.trim() || option.id : option.id;

export const buildInSessionValueOptions = (
  config:
    | {
        options?: { altValue?: string; id: string; label: string }[];
        showAltValues?: boolean;
      }
    | undefined
): ConditionalLogicOption[] | null => {
  if (!config?.options?.length) {
    return null;
  }

  return config.options.map((option) => ({
    value: resolveOptionStoredValue(option, config.showAltValues ?? false),
    label: option.label,
  }));
};

export const OPTION_FIELD_TYPES = ['select', 'multiselect'] as const;

export const isOptionFieldType = (type: string): boolean =>
  (OPTION_FIELD_TYPES as readonly string[]).includes(type);

export const DEFAULT_VALUE_FIELD_TYPES = ['text', 'textarea', 'link'] as const;

export const supportsDefaultValue = (type: string): boolean =>
  (DEFAULT_VALUE_FIELD_TYPES as readonly string[]).includes(type);

/**
 * True when the field being edited is a custom field that has already been
 * persisted — i.e. it was present in the initial, loaded-from-DB snapshot.
 * Its type (and option list) must then stay locked, because stored values
 * already recorded against it would no longer match a changed type. Custom
 * fields added earlier in the same unsaved session are NOT in the snapshot and
 * remain editable until the form is saved and reopened. See CUS-199.
 */
export const isPersistedCustomField = (
  editingFieldId: string | null,
  editingField: { isCustom?: boolean } | undefined,
  snapshotFields: Record<string, unknown> | undefined
): boolean =>
  editingFieldId != null &&
  editingField?.isCustom === true &&
  snapshotFields?.[editingFieldId] != null;

/**
 * Per-field metadata supplied by the host, keyed by field id. In-session-added
 * fields aren't in here and get derived eligibility in
 * {@link buildConditionalLogicFieldOptions}.
 *
 * Carries `label` and `type` as well, which are redundant for a canvas field
 * (the canvas is the authority on both) but are the only source of them for a
 * synthetic source, which has no canvas field to read them off.
 */
export type ExternalFieldMeta = Pick<
  ConditionalLogicOption,
  | 'allowAsSource'
  | 'allowAsTarget'
  | 'freeText'
  | 'isSynthetic'
  | 'label'
  | 'operatorOptions'
  | 'type'
>;

/**
 * Builds the conditional-logic source picker options for the dialog by merging
 * host-supplied per-field metadata with in-session editor state. Host metadata
 * wins when present (including its operator list); in-session-only fields
 * derive eligibility and operators from their field type.
 *
 * The canvas is the authority on which options exist, so a host option with no
 * field behind it is dropped — the host declares metadata for every field it
 * knows of, including ones this form does not place, and surfacing those would
 * offer rules against fields that are not on the form. The one exception is a
 * source the host marks `isSynthetic`: it has no field by definition (CUS-242's
 * T1 Category is the record's nearest Tier-1 ancestor, not something anyone
 * fills in), so it is appended verbatim and carries its own label and type.
 */
export const buildConditionalLogicFieldOptions = ({
  fields,
  fieldConfigs,
  externalFieldMetaById,
  getFieldTypeMetadata,
}: {
  fields: Record<string, FieldSummary>;
  fieldConfigs: Record<string, FieldEditorValues>;
  externalFieldMetaById: Map<string, ExternalFieldMeta>;
  getFieldTypeMetadata?: (
    type: string
  ) => FieldTypeConditionMetadata | undefined;
}): ConditionalLogicOption[] => {
  const fieldBackedOptions = Object.entries(fields).map(([id, f]) => {
    const external = externalFieldMetaById.get(id);
    if (external) {
      return {
        ...external,
        value: id,
        // The canvas wins on label and type: a renamed or retyped field must
        // read as its in-session state, not the host's pre-edit snapshot.
        label: f.name,
        type: f.type,
      };
    }

    // In-session-only field: derive eligibility from the field type;
    // fall back to "has user-typed options" when no metadata callback is
    // supplied. Custom fields are always target-eligible.
    const inSession = fieldConfigs[id];
    const meta = getFieldTypeMetadata?.(f.type);
    const hasUserOptions = (inSession?.options?.length ?? 0) > 0;
    const allowAsSource = meta?.allowAsConditionSource ?? hasUserOptions;
    // Free-text: type has no enumerable values and no user-typed options.
    const freeText =
      allowAsSource && !hasUserOptions && meta?.defaultValueOptions === null;

    return {
      value: id,
      label: f.name,
      type: f.type,
      allowAsSource,
      allowAsTarget: true,
      freeText,
      operatorOptions: allowAsSource ? meta?.operatorOptions : undefined,
    };
  });

  const syntheticOptions: ConditionalLogicOption[] = [];
  for (const [id, external] of externalFieldMetaById) {
    if (external.isSynthetic && fields[id] === undefined) {
      syntheticOptions.push({ ...external, value: id });
    }
  }

  return [...fieldBackedOptions, ...syntheticOptions];
};

const rulesValidationRefine = (
  data: {
    conditionalLogicEnabled: boolean;
    conditionalLogicRules?: { ifField: string; values: string[] }[];
  },
  zctx: z.RefinementCtx
) => {
  if (!data.conditionalLogicEnabled || !data.conditionalLogicRules?.length) {
    return;
  }
  data.conditionalLogicRules.forEach((rule, index) => {
    if (!rule.ifField) {
      zctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Field is required',
        path: ['conditionalLogicRules', index, 'ifField'],
      });
    }
    if (!rule.values || rule.values.length === 0) {
      zctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one value is required',
        path: ['conditionalLogicRules', index, 'values'],
      });
    }
  });
};

/**
 * Merge mode rejects a customer (unlocked) rule that targets the same `ifField`
 * as a locked default rule: AND-ing two rules on one field can only narrow (and
 * with disjoint values becomes unsatisfiable), so the field would silently never
 * show. The picker already hides fields used by another rule, so this is a
 * save-time backstop.
 */
const lockedConflictRefine = (
  data: {
    conditionalLogicEnabled: boolean;
    conditionalLogicRules?: { ifField: string; locked?: boolean }[];
  },
  zctx: z.RefinementCtx
) => {
  const rules = data.conditionalLogicRules;
  if (!data.conditionalLogicEnabled || !rules?.length) {
    return;
  }
  const lockedIfFields = new Set(
    rules.filter((r) => r.locked && r.ifField).map((r) => r.ifField)
  );
  if (lockedIfFields.size === 0) {
    return;
  }
  rules.forEach((rule, index) => {
    if (!rule.locked && rule.ifField && lockedIfFields.has(rule.ifField)) {
      zctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('formBuilder.formField.lockedConflictErrorMessage'),
        path: ['conditionalLogicRules', index, 'ifField'],
      });
    }
  });
};

export const sectionEditorSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
    conditionalLogicEnabled: z.boolean().default(false),
    conditionalLogicOperation: conditionalLogicOperationSchema,
    conditionalLogicRules: conditionalLogicRulesSchema,
    guidanceEnabled: z.boolean().default(false),
    guidance: z.string().optional(),
  })
  .superRefine(rulesValidationRefine);

const fieldEditorObjectSchema = z.object({
  fieldType: z.string().min(1, 'Field type is required'),
  fieldName: z.string().min(1, 'Field name is required'),
  required: z.boolean().optional().default(false),
  readOnly: z.boolean().optional().default(false),
  hidden: z.boolean().optional().default(false),
  defaultValueEnabled: z.boolean().optional().default(false),
  defaultValue: z.string().optional().default(''),
  options: z.array(optionSchema).optional().default([]),
  showAltValues: z.boolean().optional(),
  altLabel: z.string().optional(),
  conditionalLogicEnabled: z.boolean().default(false),
  conditionalLogicOperation: conditionalLogicOperationSchema,
  conditionalLogicRules: conditionalLogicRulesSchema,
  // Provenance of a code-declared default condition. `locked` → read-only
  // default: rendered non-editable and never persisted. `isDefault` →
  // unmodified editable default: not persisted until the admin edits it.
  conditionalLogicLocked: z.boolean().optional(),
  conditionalLogicIsDefault: z.boolean().optional(),
  guidanceEnabled: z.boolean().default(false),
  guidance: z.string().optional(),
});

/**
 * Option-list validation (count + label uniqueness) for `select`/`multiselect`
 * field types. Only meaningful when the admin actually edits the option list —
 * a standard field that maps to one of these types (e.g. the Tags field →
 * `multiselect`) has system-managed values and no editor option list, so this
 * refine is skipped for it (see {@link createFieldEditorSchema}). Otherwise the
 * unsatisfiable "At least 2 options" error would silently block saving.
 */
const optionsValidationRefine = (
  data: { fieldType: string; options: { id: string; label: string }[] },
  ctx: z.RefinementCtx
) => {
  if (!isOptionFieldType(data.fieldType)) {
    return;
  }
  if (data.options.length < 2) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'At least 2 options are required',
      path: ['options'],
    });
  }

  const labels = data.options.map((o) => o.label.trim().toLowerCase());
  const seen = new Set<string>();
  labels.forEach((label, index) => {
    if (seen.has(label)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Option labels must be unique',
        path: ['options', index, 'label'],
      });
    }
    seen.add(label);
  });
};

const altValuesValidationRefine = (
  data: {
    altLabel?: string;
    fieldType: string;
    options: { altValue?: string; id: string; label: string }[];
    showAltValues?: boolean;
  },
  ctx: z.RefinementCtx
) => {
  if (!isOptionFieldType(data.fieldType) || !data.showAltValues) {
    return;
  }

  if (!data.altLabel || data.altLabel.trim().length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Label value is required when show values is enabled',
      path: ['altLabel'],
    });
  }

  const seen = new Set<string>();
  data.options.forEach((option, index) => {
    const altValue = option.altValue?.trim() ?? '';
    if (altValue.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'All options must have a value when show values is enabled',
        path: ['options', index, 'altValue'],
      });

      return;
    }
    if (seen.has(altValue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Option values must be unique',
        path: ['options', index, 'altValue'],
      });
    }
    seen.add(altValue);
  });
};

const defaultValueValidationRefine = (
  data: {
    fieldType: string;
    defaultValueEnabled: boolean;
    defaultValue: string;
  },
  ctx: z.RefinementCtx
) => {
  if (
    data.defaultValueEnabled &&
    (!data.defaultValue || data.defaultValue.trim().length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Default value is required',
      path: ['defaultValue'],
    });
  }

  if (
    data.fieldType === 'url' &&
    data.defaultValue &&
    data.defaultValue.length > 0
  ) {
    try {
      const url = new URL(data.defaultValue);
      Boolean(url);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid URL',
        path: ['defaultValue'],
      });
    }
  }
};

export const fieldEditorSchema = fieldEditorObjectSchema
  .superRefine(rulesValidationRefine)
  .superRefine(lockedConflictRefine)
  .superRefine(optionsValidationRefine)
  .superRefine(altValuesValidationRefine)
  .superRefine(defaultValueValidationRefine);

export type SectionEditorValues = z.infer<typeof sectionEditorSchema>;
export type FieldEditorValues = z.infer<typeof fieldEditorSchema>;

const CYCLE_ERROR_MESSAGE =
  'One or more rules create a circular dependency. Remove or change the rule(s) that cause this.';

const SELF_REFERENCE_MESSAGE = 'A field cannot depend on itself';

const SECTION_OWN_FIELD_MESSAGE =
  'A section cannot depend on a field inside the same section';

const formatCyclePathMessage = (
  cycleVertices: string[],
  getEntityLabel?: (vertexId: string) => string
): string => {
  const labels = cycleVertices.map(
    (v) => getEntityLabel?.(v) ?? v.replace(/^(field|section):/, '')
  );

  return `${CYCLE_ERROR_MESSAGE} Cycle: ${labels.join(' → ')}.`;
};

const cycleCheckRefine =
  (entityType: 'field' | 'section', ctx: CycleCheckContext | undefined) =>
  (
    data: {
      conditionalLogicEnabled: boolean;
      conditionalLogicRules?: { ifField: string }[];
    },
    zctx: z.RefinementCtx
  ) => {
    // Run the check whenever rules exist; a disabled toggle still produces a
    // live cycle the moment it's re-enabled.
    if (!ctx || !data.conditionalLogicRules?.length) {
      return;
    }

    if (entityType === 'field') {
      data.conditionalLogicRules.forEach((rule, idx) => {
        if (rule.ifField && rule.ifField === ctx.entityId) {
          zctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SELF_REFERENCE_MESSAGE,
            path: ['conditionalLogicRules', idx, 'ifField'],
          });
        }
      });
    }

    if (entityType === 'section') {
      const fieldsInSection = new Set(ctx.containers[ctx.entityId] ?? []);
      data.conditionalLogicRules.forEach((rule, idx) => {
        if (rule.ifField && fieldsInSection.has(rule.ifField)) {
          zctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SECTION_OWN_FIELD_MESSAGE,
            path: ['conditionalLogicRules', idx, 'ifField'],
          });
        }
      });
    }

    const cycleVertices = findCycleVertices({
      ...ctx,
      entityType,
      proposedRules: data.conditionalLogicRules,
    });
    if (cycleVertices) {
      zctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatCyclePathMessage(cycleVertices, ctx.getEntityLabel),
        path: ['conditionalLogicRules'],
      });
    }
  };

/**
 * Context for the required-field guard. Kept separate from
 * {@link CycleCheckContext} — that type describes the conditional-logic graph,
 * and the guard needs the host's field summaries instead, which the graph has
 * no use for.
 */
export interface RequiredFieldCheckContext {
  /**
   * Members of the section being edited that must not be hidden — always-required
   * fields, plus field groups, which may contain one.
   */
  protectedFieldIds: string[];
  fields: Record<string, FieldSummary>;
}

/**
 * Rejects conditional logic on a section holding a field that must not be
 * hidden — one the admin cannot mark optional, or a group that may contain one
 * (CUS-381).
 *
 * A save-time backstop, not the primary guard: the section editor locks the
 * toggle, so the only way to reach this is a configuration that arrived already
 * in violation. Satisfiable in that case — removing the rules clears it, and
 * `handleSectionSubmit` then sanitises the toggle off.
 */
const requiredFieldSectionRefine =
  (ctx?: RequiredFieldCheckContext) =>
  (
    data: {
      conditionalLogicEnabled: boolean;
      conditionalLogicRules?: { ifField: string }[];
    },
    zctx: z.RefinementCtx
  ) => {
    if (
      !ctx?.protectedFieldIds.length ||
      !data.conditionalLogicEnabled ||
      !data.conditionalLogicRules?.length
    ) {
      return;
    }

    zctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: buildRequiredFieldSectionRuleError(
        ctx.protectedFieldIds,
        ctx.fields
      ),
      path: ['conditionalLogicRules'],
    });
  };

export const createSectionEditorSchema = (
  ctx?: CycleCheckContext,
  requiredFieldCtx?: RequiredFieldCheckContext
) =>
  sectionEditorSchema
    .superRefine(cycleCheckRefine('section', ctx))
    .superRefine(requiredFieldSectionRefine(requiredFieldCtx));

/**
 * Builds the field-editor schema with cycle detection.
 *
 * `isFieldTypeEditable: false` mirrors `disableFieldType` — the type select and
 * option list are locked, so the option-count and `fieldType`-required rules are
 * dropped. Left on, they're unsatisfiable and block save (RSP-6644, CUS-199).
 */
export const createFieldEditorSchema = (
  ctx?: CycleCheckContext,
  isFieldTypeEditable = true
) => {
  const base = isFieldTypeEditable
    ? fieldEditorSchema
    : fieldEditorObjectSchema
        .extend({ fieldType: z.string() })
        .superRefine(rulesValidationRefine)
        .superRefine(lockedConflictRefine)
        .superRefine(defaultValueValidationRefine);

  return base.superRefine(cycleCheckRefine('field', ctx));
};
